import { useToast } from '@chakra-ui/react';
import { PatchDataWrapper, PostDataWrapper } from '@diamond/shared/data-access';
import { UpdatePaymentProof, UploadPaymentProof } from '@diamond/shared/types';
import { showToast } from '@diamond/shared/ui';
import {
  NOTIFICATION_QUERY_KEY,
  WAITING_PAYMENTS_SUMMARY_QUERY_KEY,
} from '@diamond/sol-buyer-context';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import * as Yup from 'yup';

import { getApiErrorMessage } from '../../payment-instruction/get-api-error-message';
import {
  capturePaymentInstructionFailure,
  PaymentInstructionTelemetryContext,
} from '../../payment-instruction/payment-instruction-sentry-context';

interface UsePaymentProofFormParams {
  accessToken: string;
  paymentId?: string;
  existingProofs?: Array<{ url: string }>;
  defaultBankName?: string;
  defaultBankAccount?: string;
  onSuccess?: () => Promise<void> | void;
  skipSuccessToast?: boolean;
  telemetryContext?: PaymentInstructionTelemetryContext;
}

type PaymentProofFormData = UpdatePaymentProof & {
  bukti_bayar?: File[];
};

interface UsePaymentProofFormResult {
  form: UseFormReturn<PaymentProofFormData>;
  isSubmitting: boolean;
  handleSubmit: (values: PaymentProofFormData) => Promise<void>;
  handleRemoveFile: (index: number) => void;
  existingProofsToKeep: Array<{ url: string }>;
  files: File[];
  hasNewFiles: boolean;
  isPreviewOpen: boolean;
  previewUrl: string;
  openPreview: (url: string) => void;
  closePreview: () => void;
}

const validationSchema = Yup.object().shape({
  bank_name: Yup.string().required('Nama Bank harus diisi'),
  bank_account: Yup.string().required('Nama Pemilik Rekening harus diisi'),
  bukti_bayar: Yup.array().optional().label('File bukti pembayaran'),
});

const EMPTY_EXISTING_PROOFS: Array<{ url: string }> = [];

export const usePaymentProofForm = ({
  accessToken,
  paymentId,
  existingProofs = EMPTY_EXISTING_PROOFS,
  defaultBankName,
  defaultBankAccount,
  onSuccess,
  skipSuccessToast = false,
  telemetryContext,
}: UsePaymentProofFormParams): UsePaymentProofFormResult => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const form = useForm<PaymentProofFormData>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      bank_name: defaultBankName || '',
      bank_account: defaultBankAccount || '',
    },
  });
  const [files, setFiles] = useState<File[]>([]);
  const [existingProofsToKeep, setExistingProofsToKeep] =
    useState<Array<{ url: string }>>(existingProofs);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const watchedFiles = form.watch('bukti_bayar');

  const hasNewFiles = files.length > 0;

  // Sync existing proofs when parent data updates (e.g., after refetch)
  useEffect(() => {
    setExistingProofsToKeep(existingProofs);
  }, [existingProofs]);

  // Sync bank details when parent data updates (e.g., after refetch)
  useEffect(() => {
    if (defaultBankName && defaultBankAccount) {
      form.setValue('bank_name', defaultBankName);
      form.setValue('bank_account', defaultBankAccount);
    }
  }, [defaultBankName, defaultBankAccount, form]);

  useEffect(() => {
    if (!watchedFiles || watchedFiles.length === 0) {
      return;
    }

    setFiles((prev) => {
      const newUniqueFiles = watchedFiles.filter((file) => {
        const exists = prev.some(
          (prevFile) =>
            prevFile.name === file.name &&
            prevFile.size === file.size &&
            prevFile.lastModified === file.lastModified
        );
        return !exists;
      });

      if (newUniqueFiles.length === 0) {
        return prev;
      }

      return [...prev, ...newUniqueFiles];
    });
  }, [watchedFiles]);

  const handleRemoveFile = useCallback(
    (index: number) => {
      const removed = files[index];
      if (!removed) return;

      setFiles((prev) => prev.filter((_, i) => i !== index));

      const current = form.getValues('bukti_bayar') ?? [];
      const matchIndex = current.findIndex(
        (file) =>
          file.name === removed.name &&
          file.size === removed.size &&
          file.lastModified === removed.lastModified
      );
      if (matchIndex >= 0) {
        form.setValue(
          'bukti_bayar',
          current.filter((_, i) => i !== matchIndex),
          { shouldDirty: true, shouldValidate: true }
        );
      }
    },
    [files, form]
  );

  const uploadClient = useMemo(
    () =>
      PostDataWrapper(
        '/order/payment-proof/upload',
        accessToken,
        true,
        'buyer'
      ),
    [accessToken]
  );

  const submitProof = useCallback(
    async (values: PaymentProofFormData) => {
      if (!paymentId) {
        if (telemetryContext) {
          capturePaymentInstructionFailure(
            new Error('Payment id is missing'),
            telemetryContext,
            'missing-payment-id'
          );
        }
        showToast(toast, 'error', 'Pembayaran tidak tersedia');
        return;
      }

      const existingCount = existingProofsToKeep.length;
      if (existingCount >= 5) {
        showToast(toast, 'error', 'Maksimal 5 file telah terupload');
        return;
      }

      if (!files.length) {
        showToast(toast, 'error', 'Bukti pembayaran belum dipilih');
        return;
      }

      if (existingCount + files.length > 5) {
        showToast(
          toast,
          'error',
          `Maksimal 5 file. Anda sudah upload ${existingCount} file.`
        );
        return;
      }

      setIsSubmitting(true);

      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`file-${index}`, file, file.name);
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${accessToken}`,
        },
      };

      try {
        const uploadResponse = (await uploadClient.postData(
          formData,
          config
        )) as UploadPaymentProof;

        if (!uploadResponse?.urls?.length) {
          throw new Error('Gagal mengunggah bukti pembayaran');
        }

        const { patchData } = PatchDataWrapper(
          `/order/payment-proof/${paymentId}`,
          accessToken,
          'buyer'
        );

        await patchData({
          bank_name: values.bank_name,
          bank_account: values.bank_account,
          url: uploadResponse.urls,
        });

        if (!skipSuccessToast) {
          showToast(
            toast,
            'success',
            'Bukti pembayaran anda berhasil di kirim!'
          );
        }
        setExistingProofsToKeep((prev) => [
          ...prev,
          ...uploadResponse.urls.map((url) => ({ url })),
        ]);

        form.setValue('bank_name', values.bank_name);
        form.setValue('bank_account', values.bank_account);
        setFiles([]);
        form.setValue('bukti_bayar', []);

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [NOTIFICATION_QUERY_KEY],
          }),
          queryClient.invalidateQueries({
            queryKey: WAITING_PAYMENTS_SUMMARY_QUERY_KEY,
          }),
          onSuccess?.(),
        ]);
      } catch (error) {
        if (telemetryContext) {
          capturePaymentInstructionFailure(
            error,
            telemetryContext,
            isAxiosError(error) && error.response?.status === 409
              ? 'submit-after-expiry-rejected'
              : 'legacy-payment-proof-submit-failed'
          );
        }
        if (isAxiosError(error) && error.response?.status === 409) {
          await queryClient.invalidateQueries({
            queryKey: WAITING_PAYMENTS_SUMMARY_QUERY_KEY,
          });
        }

        const message = getApiErrorMessage(
          error,
          error instanceof Error ? error.message : 'Terjadi kesalahan'
        );
        showToast(toast, 'error', message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      accessToken,
      files,
      form,
      existingProofsToKeep.length,
      onSuccess,
      paymentId,
      skipSuccessToast,
      queryClient,
      telemetryContext,
      toast,
      uploadClient,
    ]
  );

  const openPreview = useCallback((url: string) => {
    setPreviewUrl(url);
    setIsPreviewOpen(true);
  }, []);

  const closePreview = useCallback(() => {
    setPreviewUrl('');
    setIsPreviewOpen(false);
  }, []);

  return {
    form,
    isSubmitting,
    handleSubmit: submitProof,
    handleRemoveFile,
    existingProofsToKeep,
    files,
    hasNewFiles,
    isPreviewOpen,
    previewUrl,
    openPreview,
    closePreview,
  };
};
